<!-- src/js/widgets/manager-requests/index.vue -->
<template>
  <div class="manager-requests">
    <RequestsList
      :requests="requests"
      :loading="loading"
      :selected-status="selectedStatus"
      @update:selected-status="handleStatusFilterChange"
      @row-click="openRequest"
      @delete-request="onDeleteRequest"
      @reply="onReplyClick"
    />

    <!-- Модалка деталей заявки -->
    <Dialog
      v-model:visible="isDetailOpen"
      modal
      maximizable
      :style="{ width: '90vw', maxWidth: '1200px', height: '90vh' }"
      :draggable="false"
      :header="`Заявка #${selectedRequest?.id || ''}`"
    >
      <RequestDetail
        ref="requestDetailRef"
        v-if="selectedRequest"
        :request="selectedRequest"
        @close="closeDetail"
        @status-updated="onStatusUpdated"
        @files-uploaded="onFilesUploaded"
        :loading="loading"
        @reply="onReplyFromDetail"
      />
    </Dialog>

    <!-- Модалка ответа -->
    <Dialog
      v-model:visible="isReplyOpen"
      modal
      :style="{ width: '90vw', maxWidth: '800px' }"
      :draggable="false"
      :header="`Ответ на заявку #${selectedRequest?.id || ''}`"
      @update:visible="onReplyModalClose"
    >
      <ReplyForm
        v-if="selectedRequest"
        ref="replyFormRef"
        v-model="replyMessage"
        :loading="replyLoading"
      />
      <template #footer>
        <Button
          :label="replyLoading ? 'Отправка...' : 'Отправить'"
          :icon="replyLoading ? 'pi pi-spin pi-spinner' : 'pi pi-send'"
          :disabled="!replyMessage.trim() || replyLoading"
          @click="submitReplyFromModal"
        />
      </template>
    </Dialog>

    <!-- Модалка успеха -->
    <Dialog
      v-model:visible="successDialogVisible"
      modal
      :style="{ width: '30rem' }"
      :draggable="false"
      @update:visible="onSuccessDialogClose"
    >
      <template #header>Успешно</template>
      <p>{{ successMessage }}</p>
      <template #footer>
        <Button label="OK" @click="successDialogVisible = false" />
      </template>
    </Dialog>

    <ConfirmDialog />

    <!-- Модалка ошибки -->
    <Dialog
      v-model:visible="errorDialogVisible"
      modal
      :style="{ width: '30rem' }"
      :draggable="false"
    >
      <template #header>Ошибка</template>
      <p>{{ errorDialogMessage }}</p>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { Request, RequestStatus, UpdateStatusPayload } from './types';
import { useRequestData } from './composables/useRequestData';
import RequestsList from './components/RequestsList.vue';
import RequestDetail from './components/RequestDetail.vue';
import ReplyForm from './components/ReplyForm.vue';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';

const selectedStatus = ref<RequestStatus | ''>('');
const selectedRequest = ref<Request | null>(null);
const isDetailOpen = ref(false);
const isReplyOpen = ref(false);
const replyMessage = ref('');
const successDialogVisible = ref(false);
const successMessage = ref('');
const errorDialogVisible = ref(false);
const errorDialogMessage = ref('');
const replyLoading = ref(false);
const replyFormRef = ref();
const requestDetailRef = ref();

const {
  requests,
  loading,
  error,
  loadRequests,
  loadRequestDetails,
  updateStatus,
  sendReply,
  uploadFiles,
  deleteRequest,
} = useRequestData();

const confirm = useConfirm();

watch(error, (err) => {
  if (err) {
    errorDialogMessage.value = err;
    errorDialogVisible.value = true;
  }
});

function handleStatusFilterChange(status: RequestStatus | '') {
  selectedStatus.value = status;
  const filter = status ? [status] : [];
  loadRequests({ status: filter });
}

async function openRequest(request: Request): Promise<void> {
  const fullRequest = await loadRequestDetails(request.id);
  if (fullRequest) {
    selectedRequest.value = fullRequest;
    isDetailOpen.value = true;
  } else {
    errorDialogMessage.value = 'Не удалось загрузить детали заявки';
    errorDialogVisible.value = true;
  }
}

function closeDetail(): void {
  isDetailOpen.value = false;
  selectedRequest.value = null;
}

function onReplyClick(request: Request): void {
  selectedRequest.value = request;
  isDetailOpen.value = false;
  isReplyOpen.value = true;
  replyMessage.value = '';
}

function onReplyModalClose(visible: boolean): void {
  if (!visible) {
    if (replyFormRef.value) {
      replyFormRef.value.clearMessage();
    }
    replyMessage.value = '';
  }
}

async function submitReplyFromModal() {
  if (!selectedRequest.value || !replyMessage.value.trim()) return;

  try {
    replyLoading.value = true;
    await sendReply(selectedRequest.value.id, { message: replyMessage.value });

    successMessage.value = 'Ответ отправлен пользователю';
    successDialogVisible.value = true;
    isReplyOpen.value = false;
    await loadRequests({ status: selectedStatus.value ? [selectedStatus.value] : [] });
  } catch (err) {
    // Ошибки обрабатываются через watch(error)
  } finally {
    replyLoading.value = false;
  }
}

function onSuccessDialogClose(visible: boolean): void {
  if (!visible) {
    if (selectedRequest.value) {
      isDetailOpen.value = true;
    }
  }
}

async function onStatusUpdated(status: RequestStatus): Promise<void> {
  if (!selectedRequest.value) return;
  try {
    await updateStatus(selectedRequest.value.id, { status });
    successMessage.value = 'Статус заявки обновлён';
    successDialogVisible.value = true;
    const updatedRequest = await loadRequestDetails(selectedRequest.value.id);
    if (updatedRequest) {
      selectedRequest.value = updatedRequest;
    }
  } catch (err) {
    console.error('Ошибка обновления статуса:', err);
  }
}

async function onFilesUploaded(files: File[]): Promise<void> {
  if (!selectedRequest.value) return;
  try {
    const result = await uploadFiles(selectedRequest.value.id, files);

    if (result?.success) {
      const updatedRequest = await loadRequestDetails(selectedRequest.value.id);
      if (updatedRequest) {
        selectedRequest.value = updatedRequest;
        // Очищаем форму файлов после успешной загрузки
        if (requestDetailRef.value) {
          requestDetailRef.value.clearAttachedFiles();
        }
      }
    } else {
      errorDialogMessage.value = result?.msg || 'Не удалось загрузить файлы';
      errorDialogVisible.value = true;
    }
  } catch (err) {
    errorDialogMessage.value = err instanceof Error ? err.message : 'Неизвестная ошибка';
    errorDialogVisible.value = true;
  }
}

async function onDeleteRequest(id: number): Promise<void> {
  confirm.require({
    message: 'Вы уверены, что хотите удалить заявку и всю переписку?',
    header: 'Подтверждение удаления',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    acceptClass: 'p-button-danger',
    rejectClass: 'p-button-secondary',
    accept: async () => {
      try {
        await deleteRequest(id);
        loadRequests({ status: selectedStatus.value ? [selectedStatus.value] : [] });
      } catch (err) {
        console.error('Ошибка удаления заявки:', err);
        loadRequests({ status: selectedStatus.value ? [selectedStatus.value] : [] });
      }
    },
    reject: () => {},
  });
}

function onReplyFromDetail() {
  isDetailOpen.value = false;
  isReplyOpen.value = true;
  replyMessage.value = '';
}
</script>
